/**
 * bloomreach-product-card-hover.js
 *
 * Bloomreach renders each product card inside a shadow root
 * (<br-product-card>). On hover of a .br-product-card__variant-swatch that
 * carries a data-br-variantimg URL, the card's secondary image is swapped
 * to that URL, then restored when the pointer leaves. A click on a swatch
 * opens that swatch's variant instead of the card's current link (see
 * onSwatchClick).
 *
 * Why the secondary image and not the main one: the shadow CSS
 * (settings.bloomreach_shadow_css) already has a rule that swaps to the
 * card's secondary image whenever any part of the card is hovered:
 *   .br-product-card:hover .br-product-card__image > img { display: none !important; }
 *   .br-product-card:hover .br-secondary-img { display: block !important; }
 * A swatch is a descendant of .br-product-card, so hovering a swatch
 * already triggers that rule and reveals .br-secondary-img for free — no
 * need to fight it with our own display overrides. This just changes
 * which image is sitting inside that wrapper before it's shown.
 *
 * Delegating mouseover/mouseout from `document` via event.composedPath()
 * (an earlier approach) was technically correct — those events are
 * composed and cross shadow boundaries — but proved flaky in practice.
 * This instead follows the pattern already used in layout/theme.liquid for
 * injecting shadow CSS: a MutationObserver walks the whole page (including
 * into shadow roots) and attaches mouseenter/mouseleave listeners directly
 * to each swatch the moment it appears, so there's no cross-boundary event
 * trickery involved at all.
 *
 * DEBUG MODE
 * Add ?brhoverdebug=1 to the URL (or run
 * localStorage.setItem('br_hover_debug', '1') once in the console) to log
 * every decision under the "[BR-PC-Hover]" tag. Turn off with
 * localStorage.removeItem('br_hover_debug').
 */
(function () {
  var SWATCH_CLASS = 'br-product-card__variant-swatch';
  var SECONDARY_IMAGE_CLASS = 'br-secondary-img';
  var WIRED_ATTR = 'data-br-hover-wired';
  var ORIGINAL_SRC_ATTR = 'data-br-original-src';
  var ORIGINAL_SRCSET_ATTR = 'data-br-original-srcset';
  var WARM_ATTR = 'data-br-warm-watched';

  var DEBUG =
    /[?&]brhoverdebug=1/.test(window.location.search) ||
    (function () {
      try {
        return localStorage.getItem('br_hover_debug') === '1';
      } catch (e) {
        return false;
      }
    })();

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[BR-PC-Hover]');
    console.log.apply(console, args);
  }

  if (DEBUG) log('script loaded');

  function cardElementFor(swatch, selector) {
    var root = swatch.getRootNode();
    var found = root && root.querySelector ? root.querySelector(selector) : null;
    log('lookup', selector, '-> root:', root, 'found:', found);
    return found;
  }

  function secondaryImageFor(swatch) {
    return cardElementFor(swatch, '.' + SECONDARY_IMAGE_CLASS + ' img');
  }

  // Cards with a single product image (.br-onlyone-img) have no
  // .br-secondary-img, and the shadow CSS never swaps the main image on
  // them — so on those the variant image goes into the main <img> itself.
  // Without this, hovering a swatch on such a card did nothing at all.
  function targetImageFor(swatch) {
    return secondaryImageFor(swatch) || cardElementFor(swatch, '.br-product-card__image > img');
  }

  // Warm the browser cache for every variant image of a card the moment the
  // pointer reaches its swatch row, so the first hover on each swatch shows
  // that colour instead of the previous image while it downloads.
  var preloaded = {};
  function preloadCardImages(swatch) {
    var root = swatch.getRootNode();
    if (!root || !root.querySelectorAll) return;
    Array.prototype.forEach.call(root.querySelectorAll('.' + SWATCH_CLASS + '[data-br-variantimg]'), function (s) {
      var url = s.getAttribute('data-br-variantimg');
      if (!url || preloaded[url]) return;
      preloaded[url] = true;
      var img = new Image();
      img.decoding = 'async';
      img.src = url;
    });
  }

  function onSwatchEnter(swatch) {
    log('enter swatch', swatch);
    preloadCardImages(swatch);

    var variantImg = swatch.getAttribute('data-br-variantimg');
    log('data-br-variantimg =', JSON.stringify(variantImg));
    if (!variantImg) return;

    var targetImg = targetImageFor(swatch);
    if (!targetImg) {
      log('no card image found in card root — aborting');
      return;
    }

    if (!targetImg.hasAttribute(ORIGINAL_SRC_ATTR)) {
      targetImg.setAttribute(ORIGINAL_SRC_ATTR, targetImg.src);
      // With a srcset the browser ignores .src, so the swap would be
      // invisible — park the srcset while the variant image is shown.
      if (targetImg.hasAttribute('srcset')) {
        targetImg.setAttribute(ORIGINAL_SRCSET_ATTR, targetImg.getAttribute('srcset'));
        targetImg.removeAttribute('srcset');
      }
    }
    targetImg.loading = 'eager';
    targetImg.src = variantImg;
    log('image src set to', variantImg);
  }

  function onSwatchLeave(swatch) {
    log('leave swatch', swatch);

    var targetImg = targetImageFor(swatch);
    if (targetImg && targetImg.hasAttribute(ORIGINAL_SRC_ATTR)) {
      if (targetImg.hasAttribute(ORIGINAL_SRCSET_ATTR)) {
        targetImg.setAttribute('srcset', targetImg.getAttribute(ORIGINAL_SRCSET_ATTR));
        targetImg.removeAttribute(ORIGINAL_SRCSET_ATTR);
      }
      targetImg.src = targetImg.getAttribute(ORIGINAL_SRC_ATTR);
      targetImg.removeAttribute(ORIGINAL_SRC_ATTR);
    }
  }

  // ---- Hover (mood) image: load on hover only ----
  //
  // The card template ships the second image as data-src/data-srcset, so
  // nothing downloads until the pointer is on the card. Touch devices never
  // load it. The image is only revealed once it has loaded (READY_CLASS,
  // used by bloomreach_shadow_css), so the main image never blanks out.
  var CAN_HOVER = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches);
  var READY_CLASS = 'br-secondary-ready';
  var LOAD_WIRED_ATTR = 'data-br-load-wired';

  function markReady(img) {
    var wrap = img.closest('.br-product-card__image');
    if (wrap) wrap.classList.add(READY_CLASS);
  }

  function loadSecondary(root) {
    var img = root && root.querySelector ? root.querySelector('.' + SECONDARY_IMAGE_CLASS + ' img') : null;
    if (!img) return;
    if (img.hasAttribute('data-src')) {
      if (img.hasAttribute('data-sizes')) img.setAttribute('sizes', img.getAttribute('data-sizes'));
      if (img.hasAttribute('data-srcset')) img.setAttribute('srcset', img.getAttribute('data-srcset'));
      img.setAttribute('src', img.getAttribute('data-src'));
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-sizes');
      log('loading secondary image', img.getAttribute('src'));
    }
    if (!img.getAttribute('src')) return;
    img.loading = 'eager';
    if (!img.hasAttribute(LOAD_WIRED_ATTR)) {
      img.setAttribute(LOAD_WIRED_ATTR, '');
      img.addEventListener('load', function () { markReady(img); });
    }
    if (img.complete && img.naturalWidth) markReady(img);
  }

  function watchCard(host) {
    if (!CAN_HOVER || host.hasAttribute(WARM_ATTR)) return;
    host.setAttribute(WARM_ATTR, '');
    // pointerover, not pointerenter: a Loomi re-render (swatch select) brings
    // a fresh <img> while the pointer is already on the card.
    host.addEventListener('pointerover', function () { loadSecondary(host.shadowRoot); });
  }

  // ---- Swatch click → that variant's PDP ----
  //
  // Swatches sit inside the card's <a class="br-product-card__link">, so a
  // swatch click is also a link click and the browser followed the link's
  // CURRENT href (the previously selected variant) before Bloomreach could
  // react. preventDefault() stops only that navigation — the event still
  // bubbles, so Loomi's own data-br-action="product-card-select-swatch"
  // handler runs as before. Then we navigate ourselves:
  //   1. data-br-variant-url on the swatch, if the card template sets it
  //      (exact, no waiting);
  //   2. otherwise the card link's href once Loomi has re-rendered it for
  //      the newly selected swatch (productUrl carries ?variant=<selected>);
  //   3. otherwise, after a short wait, whatever the link points at — no
  //      worse than before.
  var LINK_SELECTOR = '.br-product-card__link';
  var HREF_WAIT_MS = 400;

  function linkHref(root) {
    var link = root && root.querySelector ? root.querySelector(LINK_SELECTOR) : null;
    return link ? link.href : null;
  }

  function go(url, newTab) {
    log('navigating to', url, newTab ? '(new tab)' : '');
    if (newTab) {
      window.open(url, '_blank', 'noopener');
    } else {
      window.location.href = url;
    }
  }

  function onSwatchClick(swatch, event) {
    var root = swatch.getRootNode();
    // Only swatches that live inside a card link need rescuing.
    if (!swatch.closest(LINK_SELECTOR)) return;
    event.preventDefault();

    var newTab = event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1;
    var direct = swatch.getAttribute('data-br-variant-url');
    if (direct) {
      go(new URL(direct, window.location.href).href, newTab);
      return;
    }

    var before = linkHref(root);
    var started = Date.now();
    (function poll() {
      var now = linkHref(root);
      if (now && now !== before) return go(now, newTab);
      if (Date.now() - started >= HREF_WAIT_MS) {
        log('card link did not change after swatch select; following current href');
        if (now || before) go(now || before, newTab);
        return;
      }
      window.requestAnimationFrame(poll);
    })();
  }

  function wireSwatch(swatch) {
    if (swatch.hasAttribute(WIRED_ATTR)) return;
    swatch.setAttribute(WIRED_ATTR, '');
    log('wiring swatch', swatch);
    swatch.addEventListener('mouseenter', function () {
      onSwatchEnter(swatch);
    });
    swatch.addEventListener('mouseleave', function () {
      onSwatchLeave(swatch);
    });
    swatch.addEventListener('click', function (event) {
      onSwatchClick(swatch, event);
    });
  }

  // ---- "+N" / "−" swatch overflow toggle ----
  // Same trap as the swatches: the <button> sits inside the card's
  // <a class="br-product-card__link">, so clicking it also activated the
  // link and opened the PDP instead of expanding/collapsing the swatches.
  // preventDefault() cancels only that navigation; the click still bubbles
  // to Loomi's data-br-action="product-card-toggle-variants-expand"
  // handler, so the expand/collapse itself keeps working. Loomi re-renders
  // the card on toggle; the new button is wired by the root observer.
  var OVERFLOW_CLASS = 'br-product-card__variants-overflow';

  function wireOverflow(button) {
    if (button.hasAttribute(WIRED_ATTR)) return;
    button.setAttribute(WIRED_ATTR, '');
    button.addEventListener('click', function (event) {
      if (button.closest(LINK_SELECTOR)) event.preventDefault();
    });
  }

  var observedRoots = typeof WeakSet !== 'undefined' ? new WeakSet() : null;

  function alreadyObserved(root) {
    return observedRoots ? observedRoots.has(root) : false;
  }

  function observeRoot(root) {
    if (alreadyObserved(root)) return;
    if (observedRoots) observedRoots.add(root);
    log('observing root', root);
    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, scan);
      });
    }).observe(root, { childList: true, subtree: true });
  }

  function scan(node) {
    if (!node || node.nodeType !== 1) return;
    if (node.classList && node.classList.contains(SWATCH_CLASS)) {
      wireSwatch(node);
    }
    if (node.classList && node.classList.contains(OVERFLOW_CLASS)) {
      wireOverflow(node);
    }
    if (node.shadowRoot) {
      // Every card host, not only ones whose secondary image already exists:
      // Loomi can attach the shadow root before rendering into it.
      if (node.tagName === 'BR-PRODUCT-CARD' || node.shadowRoot.querySelector('.' + SECONDARY_IMAGE_CLASS)) watchCard(node);
      Array.prototype.forEach.call(node.shadowRoot.querySelectorAll('.' + SWATCH_CLASS), wireSwatch);
      Array.prototype.forEach.call(node.shadowRoot.querySelectorAll('.' + OVERFLOW_CLASS), wireOverflow);
      Array.prototype.forEach.call(node.shadowRoot.children, scan);
      observeRoot(node.shadowRoot);
    }
    if (node.children && node.children.length) {
      Array.prototype.forEach.call(node.children, scan);
    }
  }

  function init() {
    scan(document.documentElement);
    observeRoot(document.documentElement);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
